// Sidebar page: the account's forms with their status and numbers; create, duplicate, archive.

import { useHostActions, useHostContext } from '@waforms/app-sdk/client';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Tabs,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from '@waforms/ui';
import { Archive, ClipboardList, CopyPlus, KeyRound, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { FormSummaryDto } from '../../shared/api-types.ts';
import { CATEGORY_LABELS, FLOW_CATEGORIES, LIMITS } from '../../shared/limits.ts';
import { Callout, StatusBadge } from '../components/common.tsx';
import { useArchiveForm, useCreateForm, useDuplicateForm, useForms, useInboxes } from '../lib/api.ts';
import { count, percent, timeAgo } from '../lib/format.ts';

export function FormsList() {
  const host = useHostContext();
  const canEdit = host.currentAgent?.role === 'administrator';
  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const forms = useForms(tab);
  const inboxes = useInboxes();
  const inboxName = (id: number) => inboxes.data?.find((inbox) => inbox.id === id)?.name ?? `Inbox #${id}`;
  const noInbox = inboxes.isSuccess && inboxes.data.length === 0;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col px-6 py-6">
      <PageHeader
        title="Forms"
        description="WhatsApp forms your customers fill in without leaving the chat. Answers land here and on the contact."
        actions={
          canEdit && (
            <>
              <Button
                variant="faded"
                color="slate"
                icon={<KeyRound className="size-4" />}
                onClick={() => void navigate('/api-keys')}
              >
                API keys
              </Button>
              <Button icon={<Plus className="size-4" />} disabled={noInbox} onClick={() => setCreating(true)}>
                New form
              </Button>
            </>
          )
        }
      />
      {noInbox && (
        <Callout tone="warning" title="No WhatsApp Cloud inbox" className="mb-4">
          Forms are WhatsApp Flows, which need a WhatsApp inbox connected through the WhatsApp Cloud API.
          Connect one in Settings → Inboxes first.
        </Callout>
      )}
      <Tabs
        aria-label="Forms"
        className="mb-4"
        value={tab}
        onValueChange={setTab}
        items={[
          { value: 'active', label: 'Active' },
          { value: 'archived', label: 'Archived' },
        ]}
      />
      <Card flush>
        {forms.isPending ? (
          <div className="flex justify-center py-12 text-n-slate-11">
            <Spinner label="Loading forms" />
          </div>
        ) : forms.isError ? (
          <p role="alert" className="m-0 px-5 py-6 text-body-main text-n-ruby-11">
            Could not load forms: {forms.error.message}
          </p>
        ) : forms.data.length === 0 ? (
          <EmptyState
            icon={<ClipboardList />}
            title={tab === 'active' ? 'No forms yet' : 'Nothing archived'}
            description={
              tab === 'active'
                ? canEdit
                  ? 'Create a form, publish it, then send it from any WhatsApp conversation.'
                  : 'An administrator can create forms; you can then send them from conversations.'
                : 'Archived forms keep their submissions.'
            }
            action={
              tab === 'active' && canEdit && !noInbox ? (
                <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                  New form
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="px-5 pb-2">
            <Table>
              <THead>
                <TR>
                  <TH>Form</TH>
                  <TH>WhatsApp inbox</TH>
                  <TH>Status</TH>
                  <TH className="text-end">Sends</TH>
                  <TH className="text-end">Submissions</TH>
                  <TH className="text-end">Conversion</TH>
                  <TH className="text-end">Updated</TH>
                  {canEdit && tab === 'active' && (
                    <TH>
                      <span className="sr-only">Actions</span>
                    </TH>
                  )}
                </TR>
              </THead>
              <TBody>
                {forms.data.map((form) => (
                  <FormRow
                    key={form.id}
                    form={form}
                    inbox={inboxName(form.inboxId)}
                    actions={canEdit && tab === 'active'}
                  />
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Card>
      <CreateFormDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

function FormRow({ form, inbox, actions }: { form: FormSummaryDto; inbox: string; actions: boolean }) {
  const duplicate = useDuplicateForm();
  const archive = useArchiveForm();
  const host = useHostActions();
  const { toast } = useToast();

  return (
    <TR>
      <TD className="max-w-xs">
        <Link to={`/forms/${form.id}`} className="font-medium text-n-slate-12 no-underline hover:underline">
          {form.name}
        </Link>
        {form.publishState === 'queued' || form.publishState === 'running' ? (
          <div className="text-label-small text-n-slate-10">Publishing…</div>
        ) : form.publishState === 'failed' ? (
          <div className="text-label-small text-n-ruby-11">Last publish failed</div>
        ) : null}
      </TD>
      <TD className="whitespace-nowrap">{inbox}</TD>
      <TD className="whitespace-nowrap">
        <StatusBadge lifecycle={form.lifecycle} />
      </TD>
      <TD className="text-end tabular-nums">{count(form.sends)}</TD>
      <TD className="text-end tabular-nums">{count(form.submissions)}</TD>
      <TD className="text-end tabular-nums">{percent(form.conversionRate)}</TD>
      <TD className="whitespace-nowrap text-end">
        <time dateTime={form.updatedAt} title={new Date(form.updatedAt).toLocaleString()}>
          {timeAgo(form.updatedAt)}
        </time>
      </TD>
      {actions && (
        <TD className="whitespace-nowrap text-end">
          <Button
            size="xs"
            variant="ghost"
            color="slate"
            aria-label={`Duplicate ${form.name}`}
            title="Duplicate"
            icon={<CopyPlus className="size-3.5" />}
            loading={duplicate.isPending}
            onClick={() =>
              duplicate.mutate(form.id, {
                onSuccess: (copy) => host.toast(`Created “${copy.name}”`),
                onError: (error) =>
                  toast({ title: 'Could not duplicate', description: error.message, tone: 'error' }),
              })
            }
          />
          <Button
            size="xs"
            variant="ghost"
            color="ruby"
            aria-label={`Archive ${form.name}`}
            title="Archive"
            icon={<Archive className="size-3.5" />}
            loading={archive.isPending}
            onClick={() => {
              const published = form.publishedVersion !== null;
              if (
                !window.confirm(
                  published
                    ? `Archive “${form.name}”? WhatsApp stops opening it, including copies already sent and templates using it. Submissions are kept.`
                    : `Archive “${form.name}”? Submissions are kept.`,
                )
              ) {
                return;
              }
              archive.mutate(form.id, {
                onSuccess: () => host.toast(`Archived “${form.name}”`),
                onError: (error) =>
                  toast({ title: 'Could not archive', description: error.message, tone: 'error' }),
              });
            }}
          />
        </TD>
      )}
    </TR>
  );
}

function CreateFormDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const inboxes = useInboxes();
  const create = useCreateForm();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [inboxId, setInboxId] = useState('');
  const [categories, setCategories] = useState<string[]>(['LEAD_GENERATION']);
  const [error, setError] = useState<string | null>(null);
  const selectedInbox = inboxId || String(inboxes.data?.[0]?.id ?? '');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return setError('Give the form a name');
    if (!selectedInbox) return setError('Pick a WhatsApp inbox');
    if (categories.length === 0) return setError('Pick at least one category');
    setError(null);
    create.mutate(
      { name: name.trim(), inboxId: Number(selectedInbox), categories },
      {
        onSuccess: (form) => {
          onOpenChange(false);
          setName('');
          void navigate(`/forms/${form.id}`);
        },
        onError: (failure) => setError(failure.message),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New form"
      description="A form belongs to one WhatsApp number: WhatsApp keeps forms per business account."
      dismissible={!create.isPending}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <FormField label="Name" hint="Only your team sees it." required>
          <Input
            value={name}
            maxLength={LIMITS.formName}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </FormField>
        <FormField label="WhatsApp inbox" required>
          <Select
            value={selectedInbox}
            onChange={(event) => setInboxId(event.target.value)}
            options={(inboxes.data ?? []).map((inbox) => ({
              value: String(inbox.id),
              label: inbox.phoneNumber ? `${inbox.name} (${inbox.phoneNumber})` : inbox.name,
            }))}
          />
        </FormField>
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1 text-heading-3 text-n-slate-12">What is it for?</legend>
          <div className="grid grid-cols-2 gap-2">
            {FLOW_CATEGORIES.map((category) => (
              <Checkbox
                key={category}
                label={CATEGORY_LABELS[category]}
                checked={categories.includes(category)}
                onChange={(event) =>
                  setCategories((current) =>
                    event.target.checked
                      ? [...current, category]
                      : current.filter((entry) => entry !== category),
                  )
                }
              />
            ))}
          </div>
        </fieldset>
        {error && (
          <p role="alert" className="m-0 text-body-main text-n-ruby-11">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="faded"
            color="slate"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create form
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
